import React, {
    forwardRef,
    type ReactElement,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import ReactAvatarEditor from 'react-avatar-editor';
import Dropzone, { type DropzoneRef } from 'react-dropzone';

import { type SizeType } from '../../../features/config';
import useTranslation from '../../../features/locale/useTranslation';
import { Button } from '../../Button';
import { Icon } from '../../Icon';
import { ScaleInput } from '../../ScaleInput';
import { Spinner } from '../../Spinner';
import { Text } from '../../Typography';
import {
    type AvatarInputProps,
    type AvatarInputRef,
    type ImagePosition
} from '../AvatarInput.types';

import { AvatarPlaceholderInput } from './AvatarInputPlaceholder';

import styles from './AvatarInputEditMode.module.scss';

const ACCEPT_EXTENSIONS = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png']
};

const DEFAULT_SIZE: SizeType = 'large';

const SIZE_MAP: Record<SizeType, number> = {
    default: 60,
    small: 40,
    medium: 60,
    large: 80
};

export const AvatarInputEditMode = forwardRef<AvatarInputRef, AvatarInputProps>(
    (
        {
            size: sizeProp = DEFAULT_SIZE,
            value,
            slotProps: {
                input: inputProps,
                deleteButton: deleteButtonProps,
                avatar: avatarProps
            } = {},
            onChange,
            disabled,
            loading,
            onTouch,
            onDelete,
            onDrop,
            onDropClick,
            onBrowse,
            'aria-label': ariaLabel
        }: AvatarInputProps,
        ref
    ): ReactElement => {
        const { t } = useTranslation();
        const dropzoneRef = useRef<DropzoneRef>(null);
        const editorRef = useRef<ReactAvatarEditor>(null);

        const isControlled = value !== undefined;

        const [position, setPosition] = useState<ImagePosition | undefined>();
        const [scale, setScale] = useState(1);
        const [uploadedImage, setUploadedImage] = useState<File | null | undefined>(value);

        const deletedRef = useRef(false);
        const changedRef = useRef(false);

        const isDisabled = disabled || loading;

        const getParsedImage = () => {
            const image = editorRef.current?.getImageScaledToCanvas().toDataURL();
            return {
                img: image?.split(',')?.[1] || '',
                name: uploadedImage?.name || '',
                type: uploadedImage?.type || ''
            };
        };

        useImperativeHandle(ref, () => ({
            getParsedImage,
            deleted: deletedRef.current && !uploadedImage,
            changed: changedRef.current
        }));

        const handleScaleChange = (newScale: number) => {
            changedRef.current = true;
            setScale(newScale);
            onTouch?.();
        };

        const updateImage = (image: File | null) => {
            if (!isControlled) {
                setUploadedImage(image);
            }
            onChange?.(image);
        };

        const handlePositionChange = (e: ImagePosition) => {
            changedRef.current = true;
            setPosition(e);
            onTouch?.();
        };

        const handleDrop = (acceptedFiles: File[]) => {
            changedRef.current = true;
            updateImage(acceptedFiles[0]);
            onTouch?.();
            onDrop?.();
        };

        const handleDelete = () => {
            changedRef.current = true;
            deletedRef.current = true;
            updateImage(null);
            onTouch?.();
            onDelete?.();
        };

        const handleBrowse = () => {
            dropzoneRef?.current?.open();
            onBrowse?.();
        };

        const handleDropClick = () => {
            onDropClick?.();
        };

        useEffect(() => {
            if (isControlled) {
                setUploadedImage(value);
            }
        }, [value, isControlled]);

        const size = SIZE_MAP[sizeProp || DEFAULT_SIZE];

        const initialImage = deletedRef.current ? null : avatarProps?.image;

        const imageToRender = uploadedImage ?? initialImage;

        return (
            <div className={styles.root}>
                <div className={styles.dragDrop}>
                    <Dropzone
                        accept={ACCEPT_EXTENSIONS}
                        ref={dropzoneRef}
                        onDrop={handleDrop}
                        multiple={false}
                        noClick={!!imageToRender}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <div {...getRootProps()} className={styles.dropSectionRoot}>
                                <input
                                    aria-label={ariaLabel}
                                    {...inputProps}
                                    {...getInputProps()}
                                />
                                {imageToRender || loading ? (
                                    <div className={styles.imagePreview}>
                                        <div className={styles.adjustmentEditor}>
                                            <div className={styles.adjustmentBorder} />
                                            {loading && !imageToRender && (
                                                <div
                                                    className={styles.spinner}
                                                    style={{
                                                        width: size,
                                                        height: size
                                                    }}
                                                >
                                                    <Spinner size={size && size / 2} />
                                                </div>
                                            )}
                                            {!loading && imageToRender && (
                                                <ReactAvatarEditor
                                                    borderRadius={size}
                                                    color={[255, 255, 255, 0.8]}
                                                    border={0}
                                                    height={size}
                                                    width={size}
                                                    image={imageToRender}
                                                    onPositionChange={handlePositionChange}
                                                    position={position}
                                                    ref={editorRef}
                                                    scale={scale}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <AvatarPlaceholderInput
                                        disabled={disabled}
                                        onClick={handleDropClick}
                                    />
                                )}
                            </div>
                        )}
                    </Dropzone>
                    <div>
                        <div className={styles.dragDropText}>
                            <Text type="h4">{t('Drag & Drop')}</Text> <Text>{t('or')}</Text>
                        </div>
                        <Button type="ghost" size="medium" onClick={handleBrowse}>
                            {t('Browse your computer')}
                        </Button>
                    </div>
                </div>
                {imageToRender && (
                    <div className={styles.controls}>
                        <ScaleInput
                            className={styles.adjustmentScale}
                            name="scale"
                            onChange={handleScaleChange}
                            min="1"
                            max="4"
                            step="0.01"
                            scale={scale}
                            disabled={isDisabled}
                        />
                        <Button
                            type="ghost"
                            color="error"
                            onClick={handleDelete}
                            {...deleteButtonProps}
                            disabled={isDisabled || deleteButtonProps?.disabled}
                        >
                            <Icon name="s-icon-trash-can" />
                            {t('Delete')}
                        </Button>
                    </div>
                )}
            </div>
        );
    }
);

AvatarInputEditMode.displayName = 'AvatarInputEditMode';
